import { isMobileSafari, isSafari } from 'react-device-detect';
import { BehaviorSubject } from 'rxjs';

import { DCM, DOCS, IMAGE, THREE, VIDEO } from '../models/Resource';

export const defaultReceieverFields = {
  receiverEmail: '',
  receiverName: '',
};

export default class ShareLinkBloc {
  loading$ = new BehaviorSubject(false);
  shareLink$ = new BehaviorSubject(null);
  receiverFields$ = new BehaviorSubject(defaultReceieverFields);
  loadingSendShareEmail$ = new BehaviorSubject(false);

  mainResource$ = new BehaviorSubject({});
  childResources$ = new BehaviorSubject([]);

  constructor({ dialogRepository, errorRepository, patientRepository }) {
    this.dialogRepository = dialogRepository;
    this.errorRepository = errorRepository;
    this.patientRepository = patientRepository;
  }

  reset() {
    this.loading$.next(false);
    this.shareLink$.next(null);
  }

  close() {
    this.dialogRepository.toggleDialog(false);
    // Add a small delay so that the UI doesn't change before closing
    setTimeout(() => this.reset(), 1000);
  }

  async getShareLink(resourceId) {
    this.loading$.next(true);
    try {
      const mainResource = await this.patientRepository.getViewerLink(resourceId);
      const { viewerLink } = mainResource;

      const record = await this.patientRepository.getRecord(resourceId);
      this.mainResource$.next(record);

      const fileType = record?.type;
      let childResources = [];

      if (fileType === DCM || ![VIDEO, IMAGE, THREE, 'pdf', DOCS].includes(fileType)) {
        childResources = await this.patientRepository.getChildResourcesLink(resourceId);
        if (childResources?.length) {
          this.childResources$.next(
            (childResources || []).map((resource) => ({
              description: resource.description,
              id: `share-link--child-resource--${record.id}--${resource.id}`,
              viewerLink: resource.viewerLink,
            }))
          );
        }
      }

      if (viewerLink) {
        this.shareLink$.next(viewerLink);
      } else {
        this.errorRepository.error$.next({ message: 'Failed to get share link for the selected study', severity: 'error' });
        this.close();
      }
    } catch (error) {
      if (error.name !== 'NotAllowedError') {
        this.errorRepository.error$.next({ message: 'Failed to get share link for the selected study', severity: 'error' });
        this.close();
      }
    } finally {
      this.loading$.next(false);
    }
  }

  async getShareVideoReport(resourceId) {
    this.loading$.next(true);
    try {
      const videoReport = await this.patientRepository.getVideoReportLink(resourceId);
      if (videoReport?.viewerLink) {
        this.shareLink$.next(videoReport.viewerLink);
        this.loading$.next(false);
      } else {
        this.errorRepository.error$.next({ message: 'Video report is not found for this study', severity: 'error' });
        this.close();
      }
    } catch (error) {
      if (error.name !== 'NotAllowedError') {
        this.errorRepository.error$.next({ message: error.message || 'Failed to get share link for the video report', severity: 'error' });
        this.close();
      }
    }
  }

  async copyLinkToClipboard(link) {
    await navigator.clipboard?.writeText(link);
    this.errorRepository.error$.next({ message: 'Link copied to clipboard', severity: 'success' });
  }

  updateReceiverField(field, value) {
    if (!field) return;
    this.receiverFields$.next({
      ...this.receiverFields$.getValue(),
      [field]: value,
    });
  }

  async sendShareEmail(resourceId) {
    const { receiverEmail, receiverName } = this.receiverFields$.getValue();
    try {
      this.loadingSendShareEmail$.next(true);
      await this.patientRepository.shareReportEmail(receiverEmail, receiverName, resourceId);
      this.errorRepository.error$.next({ message: 'Your video report shared!', severity: 'success' });
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message || 'Failed to share report via email. Please try again.',
        severity: 'error',
      });
    } finally {
      this.loadingSendShareEmail$.next(false);
    }
  }
}
